/**
 * Interior layer resolution (secondary geometry) via polygon booleans. Each
 * free element takes part by its type's floor role (`freeElementTypes`):
 *
 * - carves (interior walls): difference from the room. Removes floor area
 *   AND adds perimeter, since it carves a hole whose ring is boundary.
 * - covers (counters): intersection with the solid floor, as countertop.
 * - every built-in standing on the floor (a counter, a cabinet, a bathtub,
 *   an appliance set into cabinets): intersection with the solid floor, then
 *   removed from the finished floor. Removes flooring coverage but NOT total
 *   floor area, and adds no perimeter.
 * - retypes (flooring): intersection with the finished floor, retyping that
 *   sub-region. Top-most (last in array) wins where they overlap.
 *
 * Everything is clipped to the resolved room, so interior rects may be placed
 * anywhere: only the part inside the room counts.
 */

import polygonClipping, { MultiPolygon as PcMP, Polygon as PcPoly } from 'polygon-clipping';
import { floorCoveringElements } from './builtIns';
import { type FloorRole, freeElementType, retypesFloor } from './freeElementTypes';
import { freeElementRing, multiPolygonArea, toPcRing } from './geometry';
import { boundarySegments, pcToPolygons, roomFootprint, Segment, toPcMultiPolygon } from './resolve';
import { FloorPlanFreeElement, FloorPlanRoom, Point, roomCeilingHeight, roomFloorType, roomFreeElements } from './schemas';

export interface FloorRegion {
  type: string;
  polygons: Point[][][];
  area: number;
}

export interface Scene {
  /** Room minus walls: the solid floor footprint (with wall holes). */
  roomSolid: Point[][][];
  /** Floor types over the floored area, painter-resolved. */
  floors: FloorRegion[];
  /** Counter regions clipped to the room. */
  counters: Point[][][];
  counterArea: number;
  /** Everything built in that stands on the floor, counters included, clipped to the room: floor that is never finished. */
  unfloored: Point[][][];
  unflooredArea: number;
  /** Wall regions clipped to the room (internal walls). */
  walls: Point[][][];
  /** Boundary segments (outer + wall holes), each a wall up to the ceiling. */
  segments: Segment[];
}

const footprint = (element: FloorPlanFreeElement): PcPoly => [toPcRing(freeElementRing(element))];

const footprintsWithRole = (elements: readonly FloorPlanFreeElement[], role: FloorRole): PcPoly[] =>
  elements.filter(element => freeElementType(element).floorRole === role).map(footprint);

const unionAll = (polygons: PcPoly[]): PcMP => polygonClipping.union(polygons[0], ...polygons.slice(1));

/** The room's solid floor with the interior walls carved out of it, and those walls clipped to the room. */
function carveWalls(room: PcMP, walls: PcPoly[]): { solid: PcMP; walls: PcMP } {
  if (!walls.length || !room.length) return { solid: room, walls: [] };
  const clipped = polygonClipping.intersection(unionAll(walls), room);
  return { solid: polygonClipping.difference(room, clipped), walls: clipped };
}

/** The union of `footprints`, clipped to the solid floor. */
const clipToFloor = (footprints: PcPoly[], floor: PcMP): PcMP =>
  footprints.length && floor.length ? polygonClipping.intersection(unionAll(footprints), floor) : [];

/**
 * The floor types over the finished floor, painter-resolved: the top-most
 * (last) flooring claims its area first, and the room's own floor type takes
 * what none claims.
 */
function floorTypeRegions(shape: FloorPlanRoom, finished: PcMP): FloorRegion[] {
  const perType = new Map<string, PcMP>();
  const addType = (type: string, region: PcMP) => perType.set(type, perType.has(type) ? polygonClipping.union(perType.get(type)!, region) : region);
  let unclaimed = finished;
  for (const flooring of roomFreeElements(shape).filter(retypesFloor).reverse()) {
    if (!unclaimed.length) break;
    const region = polygonClipping.intersection(footprint(flooring), unclaimed);
    if (!region.length) continue;
    addType(flooring.floorType || roomFloorType(shape), region);
    unclaimed = polygonClipping.difference(unclaimed, region);
  }
  if (unclaimed.length) addType(roomFloorType(shape), unclaimed);
  return [...perType.entries()].map(([type, region]) => {
    const polygons = pcToPolygons(region);
    return { type, polygons, area: multiPolygonArea(polygons) };
  });
}

export function resolveScene(shape: FloorPlanRoom): Scene {
  const elements = roomFreeElements(shape);
  const { solid, walls } = carveWalls(toPcMultiPolygon(roomFootprint(shape)), footprintsWithRole(elements, 'carves'));
  const roomSolid = pcToPolygons(solid);
  const counters = pcToPolygons(clipToFloor(footprintsWithRole(elements, 'covers'), solid));
  const unflooredPc = clipToFloor(floorCoveringElements(elements).map(footprint), solid);
  const unfloored = pcToPolygons(unflooredPc);
  const finished = unflooredPc.length && solid.length ? polygonClipping.difference(solid, unflooredPc) : solid;
  return {
    roomSolid,
    floors: floorTypeRegions(shape, finished),
    counters,
    counterArea: multiPolygonArea(counters),
    unfloored,
    unflooredArea: multiPolygonArea(unfloored),
    walls: pcToPolygons(walls),
    segments: boundarySegments(roomSolid, roomCeilingHeight(shape)),
  };
}
